import { LocalModelMutationData, ModelEvents, ModelOperation, ModelOperationBatch, ModelTransaction } from '@deqo/podopolis';
import { injectable } from 'inversify';
import { idGenerator, uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { ActiveObjectWithEvents, resolve } from '../../Container';
import { EditorPodSession } from '../EditorPodSession';
import { AnimateHack } from '../visualization/AnimateHack';

const { rolling } = ModelTransaction;
const { $xnoop, $cappend, $ctruncate } = ModelOperation.codes;

const moopIntentKey = (moop: ModelOperation) => {
    // This is mostly identical to moop fingerprint except for append/truncate where it focuses only on the target.  If you can't figure out
    // why this is ask Steve, if this is Steve reading then you got up at 4:45am because of Conrad and have no energy, rekt.
    switch (moop[0]) {
        case $cappend:
        case $ctruncate:
            return `${moop[0]}/${moop[2]}`;
        default:
            return ModelOperation.fingerprint(moop);
    }
};

const isNoopOnly = ({ applyBatch }: LocalModelMutationData) => {
    const nonNoop = applyBatch.find(([code]) => code !== $xnoop);
    return !nonNoop;
};

const sequentialKey = idGenerator('moop');

class HistoryEntry implements HistoryItem {
    public readonly id = uuid();
    constructor(
        public readonly label: string,
        public readonly undoBatch: readonly ModelOperation[],
        public readonly redoBatch: readonly ModelOperation[],
    ) { }

    public get empty() {
        return (this.undoBatch.length + this.redoBatch.length) === 0;
    }
}

type ModelOperationWithOrder = readonly [ModelOperation, number];

class HistoryEntryBuilder {

    public key: string | null = null;

    private undo = new Map<string, ModelOperationWithOrder>();
    private undoCounter = 0;

    private redo = new Map<string, ModelOperationWithOrder>();
    private redoCounter = 0;

    public append(data: LocalModelMutationData) {
        this.key = data.key;
        this.appendUndo(data.revertBatch);
        this.appendRedo(data.applyBatch);
    }

    public compile() {
        const undoBatch = Array.from(this.undo.values()).sort((a, b) => b[1] - a[1]).map(x => x[0]);
        const redoBatch = Array.from(this.redo.values()).sort((a, b) => a[1] - b[1]).map(x => x[0]);
        const label = `${this.key || 'Action'} (${undoBatch.length})`;
        return new HistoryEntry(label, undoBatch, redoBatch);
    }

    private appendUndo(batch: ModelOperationBatch) {
        for (const moop of batch) {
            const mwo = [moop, this.undoCounter++] as const;
            if (moop[0] === $xnoop) {
                // Ignore noop moops
                continue;
            }
            else if (moop[0] === $cappend || moop[0] === $ctruncate) {
                this.appendAllWritesWin(this.undo, mwo);
            }
            else {
                this.appendFirstWriteWins(this.undo, mwo);
            }
        }
    }

    private appendRedo(batch: ModelOperationBatch) {
        for (const moop of batch) {
            const mwo = [moop, this.redoCounter++] as const;
            if (moop[0] === $cappend || moop[0] === $ctruncate) {
                this.appendAllWritesWin(this.redo, mwo);
            }
            else {
                this.appendLastWriteWins(this.redo, mwo);
            }
        }
    }

    private appendFirstWriteWins(entries: Map<string, ModelOperationWithOrder>, mwo: ModelOperationWithOrder) {
        const ik = moopIntentKey(mwo[0]);
        if (entries.has(ik)) return;
        entries.set(ik, mwo);
    }

    private appendLastWriteWins(entries: Map<string, ModelOperationWithOrder>, mwo: ModelOperationWithOrder) {
        const ik = moopIntentKey(mwo[0]);
        entries.set(ik, mwo);
    }

    private appendAllWritesWin(entries: Map<string, ModelOperationWithOrder>, mwo: ModelOperationWithOrder) {
        const ik = sequentialKey();
        entries.set(ik, mwo);
    }
}

export interface HistoryItem {
    readonly id: string;
    readonly label: string;
    readonly undoBatch: readonly ModelOperation[];
    readonly redoBatch: readonly ModelOperation[];
}

@injectable()
export class HistoryManager extends ActiveObjectWithEvents<{ 'updated': any }> {

    @resolve(EditorPodSession) private pod!: EditorPodSession;

    public start() {
        this.burden(this.pod.on('modelMutation', this.onModelMutation));
    }

    public get futureItems() {
        return this.future as readonly HistoryItem[];
    }

    public get pastItems() {
        return this.past as readonly HistoryItem[];
    }

    public get nextItem() {
        return this?.next?.compile();
    }

    private future: HistoryEntry[] = [];
    private past: HistoryEntry[] = [];
    private next = null as HistoryEntryBuilder;
    private nextMutationIsUndoOrRedo = false;

    public get futureCount(): number {
        return this.future.length;
    }

    public get pastCount(): number {
        return this.past.length;
    }

    public clear(): void {
        this.past = [];
        this.future = [];
    }

    public redo(): boolean {
        if (!this.future.length) {
            return false;
        }
        const mttn = this.future.pop();
        this.nextMutationIsUndoOrRedo = true;
        AnimateHack.animate = false;
        this.pod.mutate(m => {
            m.strict = false;
            m.execAll(mttn.redoBatch);
        });
        AnimateHack.animate = true;
        this.events.emit('updated', {});
        this.past.push(mttn);
    }

    public undo(): boolean {
        if (!this.next && !this.past.length) {
            return false;
        }
        this.closeNext();
        const mttn = this.past.pop();
        this.nextMutationIsUndoOrRedo = true;
        AnimateHack.animate = false;
        this.pod.mutate(m => {
            m.strict = false;
            m.execAll(mttn.undoBatch);
        });
        AnimateHack.animate = true;
        this.future.push(mttn);
        this.events.emit('updated', {});
        return true;
    }

    public cancelNextIfKey(key: string): boolean {
        if (this.next?.key === key && this.undo()) {
            // Remove from future as its a "cancel"
            this.future.pop();
            return true;
        }
        return false;
    }

    private closeNext() {
        if (this.next) {
            const item = this.next.compile();
            if (item.empty) return;
            this.past.push(item);
            this.next = null;
        }
    }

    private onModelMutation = ([source, data]: ModelEvents['modelMutation']) => {
        // We only care about local mutations obv
        if (source === 'remote') return;
        // We also only care about local mutations that HAVE non-noop operations
        if (isNoopOnly(data)) return;

        if (data.metadata.history === 'skip') return;
        if (this.nextMutationIsUndoOrRedo) {
            this.nextMutationIsUndoOrRedo = false;
        }
        else {
            // If key matches next, we should continue this entry
            if (this.next?.key === data.key) {
                this.next.append(data);
            }
            // Otherwise, close if open
            else {
                this.closeNext();
                // Open new unless only noop
                this.next = new HistoryEntryBuilder();
                this.next.append(data);
            }
            // Regardless of key clear future on any change that isn't an undo/redo
            this.future = [];
        }

        this.events.emit('updated', {});
    };
}
